import { LucideIcon, ExternalLink, ChevronRight } from "lucide-react";
import React from "react";
import Button from "../ui/Button";
import SectionHeader from "../layout/SectionHeader";
import { cn } from "../../utils/cn";

export interface ProductBenefit {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface ProductFeature {
  icon: LucideIcon;
  title: string;
  description: string;
}

export interface ProductFlowStep {
  icon: LucideIcon;
  label: string;
}

interface ProductOverviewProps {
  icon: LucideIcon;
  productName: string;
  tagline: string;
  description: string;
  ctaLabel: string;
  onCta: () => void;
  ctaLoading?: boolean;
  benefitsTitle: string;
  benefitsSubtitle: string;
  benefits: ProductBenefit[];
  featuresTitle: string;
  featuresSubtitle: string;
  features: ProductFeature[];
  flowTitle: string;
  flowSubtitle: string;
  flow: ProductFlowStep[];
  accentClass?: string;
}

/**
 * Página comercial de apresentação de produto (LeadFlow / Virtus WhatsApp) —
 * estrutura compartilhada entre os dois, já que ambos têm exatamente a mesma
 * finalidade (explicar o produto + acessar via SSO), só o conteúdo muda.
 * Reaproveita o Design System já consolidado (Card branco/border-purple-200/
 * rounded-xl/shadow-sm, SectionHeader, Button compartilhado) — nenhum
 * componente/token visual novo foi introduzido.
 */
const ProductOverview: React.FC<ProductOverviewProps> = ({
  icon: Icon,
  productName,
  tagline,
  description,
  ctaLabel,
  onCta,
  ctaLoading = false,
  benefitsTitle,
  benefitsSubtitle,
  benefits,
  featuresTitle,
  featuresSubtitle,
  features,
  flowTitle,
  flowSubtitle,
  flow,
  accentClass = "bg-purple-600",
}) => {
  return (
    <div className="w-full flex flex-col gap-2">
      {/* Hero — layout centralizado: mais robusto que uma linha
          icon+texto+botão (não depende de alinhamento flex-row/col por
          breakpoint) e dá mais peso visual ao nome do produto, que é a
          informação mais importante da página. */}
      <div className="bg-white rounded-xl border border-purple-200 p-6 sm:p-8 shadow-xs flex flex-col items-center text-center">
        <div
          className={cn(
            "w-16 h-16 rounded-xl flex items-center justify-center text-white shadow-xs mb-4 border border-white/20",
            accentClass,
          )}
        >
          <Icon className="h-8 w-8" />
        </div>

        <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-gray-900">
          {productName}
        </h1>
        <p className="text-sm sm:text-base text-gray-500 font-medium mt-2 max-w-xl">
          {tagline}
        </p>

        <Button
          variant="primary"
          size="md"
          icon={<ExternalLink className="h-4 w-4" />}
          onClick={onCta}
          isLoading={ctaLoading}
          className="rounded-lg font-bold mt-5"
        >
          {ctaLabel}
        </Button>

        <p className="text-sm text-gray-500 leading-relaxed mt-6 pt-6 border-t border-purple-100 max-w-2xl">
          {description}
        </p>
      </div>

      {/* Benefícios */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
        <SectionHeader
          icon={<Icon className="h-5 w-5" />}
          title={benefitsTitle}
          subtitle={benefitsSubtitle}
          iconBgClass={accentClass}
        />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {benefits.map((benefit, i) => (
            <div
              key={i}
              className="p-3 border border-purple-200 rounded-lg bg-gray-50/50"
            >
              <div className="w-9 h-9 rounded-lg bg-purple-50 text-purple-600 border border-purple-200 flex items-center justify-center mb-2">
                <benefit.icon className="h-4 w-4" />
              </div>
              <h4 className="text-sm font-black text-gray-900 tracking-tight">
                {benefit.title}
              </h4>
              <p className="text-xs text-gray-500 leading-relaxed mt-1">
                {benefit.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Recursos */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
        <SectionHeader
          icon={<Icon className="h-5 w-5" />}
          title={featuresTitle}
          subtitle={featuresSubtitle}
          iconBgClass={accentClass}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {features.map((feature, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 border border-purple-200 rounded-lg"
            >
              <div className="w-9 h-9 shrink-0 rounded-lg bg-purple-50 text-purple-600 border border-purple-200 flex items-center justify-center">
                <feature.icon className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <h4 className="text-xs font-black text-gray-900 uppercase tracking-wide">
                  {feature.title}
                </h4>
                <p className="text-xs text-gray-500 leading-relaxed mt-0.5">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Como funciona / Ecossistema */}
      <div className="bg-white rounded-xl border border-purple-200 p-3 shadow-xs">
        <SectionHeader
          icon={<Icon className="h-5 w-5" />}
          title={flowTitle}
          subtitle={flowSubtitle}
          iconBgClass={accentClass}
        />
        <div className="flex flex-row flex-wrap items-center justify-center gap-2 py-2">
          {flow.map((step, i) => (
            <React.Fragment key={i}>
              <div className="flex flex-col items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-50/50 border border-purple-200 min-w-[110px] flex-1 max-w-[160px]">
                <div className="w-8 h-8 rounded-lg bg-purple-50 text-purple-600 border border-purple-200 flex items-center justify-center">
                  <step.icon className="h-4 w-4" />
                </div>
                <span className="text-[10px] font-bold text-gray-700 uppercase tracking-wide text-center">
                  {step.label}
                </span>
              </div>
              {i < flow.length - 1 && (
                <ChevronRight className="h-4 w-4 text-purple-300 shrink-0" />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* CTA final */}
      <div
        className={cn(
          "rounded-xl p-4 shadow-xs flex flex-col sm:flex-row items-center justify-between gap-3 text-white",
          accentClass,
        )}
      >
        <p className="text-sm font-bold text-center sm:text-left">{tagline}</p>
        <Button
          variant="secondary"
          size="md"
          icon={<ExternalLink className="h-4 w-4" />}
          onClick={onCta}
          isLoading={ctaLoading}
          className="bg-white! text-gray-900! hover:bg-gray-100! rounded-lg font-bold shrink-0"
        >
          {ctaLabel}
        </Button>
      </div>
    </div>
  );
};

export default ProductOverview;
